import { createFileRoute, Link } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { TravelpayoutsWidget } from "@/components/travelpayouts-widget";
import { SearchClickStats } from "@/components/search-click-stats";
import { TOP_DEPARTURE_CITIES, TOP_DESTINATIONS, THEMES, cityToGen, countryGen, departureCityLabel } from "@/lib/seo-routes";
import { findAirport, allCountries, countrySlug } from "@/lib/airports";
import { buildMeta } from "@/lib/seo";
import { getInitialOrigin } from "@/lib/initial-origin.functions";
import { breadcrumbScript } from "@/lib/breadcrumbs";

export const Route = createFileRoute("/tanie-loty")({
  loader: async () => {
    const origin = await getInitialOrigin({ data: { page: "/tanie-loty" } }).catch(() => ({ from: "WAR" as const, reason: "default" as const }));
    return { initialFrom: origin.from };
  },
  head: () => {
    const base = buildMeta({
      title: "Tanie loty z Polski — porównaj oferty | Tanie Latanie",
      // ≤160 znaków, żeby cały opis mieścił się w snippet-cie Google.
      description:
        "Porównaj loty z polskich lotnisk w Aviasales. Wybierz daty i kierunek, sprawdź taryfę, bagaż oraz cenę u sprzedawcy przed zakupem.",
      path: "/tanie-loty",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Tanie loty",
            description: "Porównywarka tanich lotów z Polski do całego świata.",
            url: "https://tanielatanie.pl/tanie-loty",
          }),
        },
        breadcrumbScript([
          { name: "Strona główna", path: "/" },
          { name: "Tanie loty", path: "/tanie-loty" },
        ]),
      ],
    };
  },
  component: PylarPage,
});

function PylarPage() {
  const { initialFrom } = Route.useLoaderData();
  const departures = TOP_DEPARTURE_CITIES.map((c) => findAirport(c)).filter((a): a is NonNullable<typeof a> => Boolean(a));
  const destinations = TOP_DESTINATIONS.slice(0, 32).map((c) => findAirport(c)).filter((a): a is NonNullable<typeof a> => Boolean(a));
  const countries = allCountries().slice(0, 40);

  return (
    <div className="min-h-screen">
      <Header />

      <section className="container mx-auto max-w-5xl px-4 pt-8 pb-6">
        <Link to="/" className="mb-4 inline-block text-sm text-primary hover:underline">Tanie Latanie — poradnik i wyszukiwarka</Link>
        <h1 className="font-display text-3xl font-bold leading-tight md:text-5xl">
          Tanie loty z Polski — <span className="text-gradient-sunset">porównaj ceny biletów</span>
        </h1>
        <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
          Porównaj oferty w wyszukiwarce Aviasales. Wybierz daty i lotniska, a następnie potwierdź cenę oraz zakres bagażu u sprzedawcy.
        </p>
        <div className="mt-6">
          <TravelpayoutsWidget />
          <SearchClickStats />
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 py-8">
        <h2 className="mb-4 font-display text-2xl font-bold">Czym są tanie loty?</h2>
        <div className="prose prose-invert max-w-none text-foreground/85">
          <p>
            Tanie loty to bilety lotnicze oferowane w cenach znacznie niższych niż standardowe taryfy linii sieciowych — najczęściej dzięki modelowi tzw. low-cost (Ryanair, Wizz Air, easyJet), promocjom sezonowym lub last minute. Z Polski tanie loty wykonują też tradycyjne linie (LOT, Lufthansa, KLM), które obniżają ceny przy wcześniejszej rezerwacji lub poza szczytem.
          </p>
          <p>
            Nie ma dnia tygodnia ani wyprzedzenia zakupu gwarantującego najniższą cenę. Porównaj kilka terminów, lotniska alternatywne oraz koszt całej podróży. Ceny zapisane w sekcji okazji mogą się zmienić; aktualną dostępność sprawdź dla własnych dat.
          </p>
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 py-8">
        <h2 className="mb-5 font-display text-2xl font-bold">Tanie loty z polskich miast</h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
          {departures.map((a) => (
            <Link
              key={a.code}
              to="/loty/$city"
              params={{ city: a.code.toLowerCase() }}
              className="rounded-lg border border-border/50 bg-card/40 px-4 py-3 text-sm font-medium transition hover:border-primary/40 hover:bg-card/70"
            >
              Tanie loty z {departureCityLabel(a.code)}
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 py-8">
        <h2 className="mb-5 font-display text-2xl font-bold">Popularne kierunki</h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
          {destinations.map((a) => (
            <Link
              key={a.code}
              to="/loty/$from/$to"
              params={{ from: "waw", to: a.code.toLowerCase() }}
              className="rounded-lg border border-border/50 bg-card/40 px-4 py-3 text-sm font-medium transition hover:border-primary/40 hover:bg-card/70"
            >
              <span className="block">Loty do {cityToGen(a.city)}</span>
              <span className="block text-xs text-muted-foreground">z Warszawy · {a.code}</span>
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 py-8">
        <h2 className="mb-5 font-display text-2xl font-bold">Tanie loty według typu wyjazdu</h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
          {THEMES.map((t) => (
            <Link
              key={t.slug}
              to="/loty/temat/$slug"
              params={{ slug: t.slug }}
              className="rounded-lg border border-border/50 bg-card/40 px-4 py-3 text-sm font-medium transition hover:border-primary/40 hover:bg-card/70"
            >
              {t.label}
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto max-w-5xl px-4 py-8">
        <h2 className="mb-5 font-display text-2xl font-bold">Tanie loty według kraju</h2>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
          {countries.map((c) => (
            <Link
              key={c}
              to="/loty/kraj/$slug"
              params={{ slug: countrySlug(c) }}
              className="rounded-lg border border-border/50 bg-card/40 px-4 py-3 text-sm font-medium transition hover:border-primary/40 hover:bg-card/70"
            >
              Loty do {countryGen(c)}
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto max-w-4xl px-4 py-10">
        <h2 className="mb-5 font-display text-2xl font-bold">Najczęściej zadawane pytania</h2>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="glass rounded-lg p-4">
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="mt-2 text-muted-foreground whitespace-pre-line">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

const FAQ = [
  { q: "Kiedy kupować tanie loty?", a: "Nie ma jednego najlepszego momentu dla wszystkich tras. Zacznij porównywać wcześniej przy sztywnych terminach i sprawdź kilka dat, jeśli możesz zmienić plan." },
  { q: "Który dzień tygodnia jest najtańszy na lot?", a: "To zależy od połączenia, sezonu i dostępności. Porównaj dni robocze z weekendem, ale uwzględnij także noclegi i dojazd." },
  { q: "Ile kosztują tanie loty z Polski?", a: "Aktualną cenę sprawdzisz dla konkretnych dat, lotnisk i liczby pasażerów. Ceny zapisane w sekcji okazji nie stanowią gwarancji dostępności." },
  { q: "Czy można znaleźć lot bezpośredni?", a: "Dostępność zależy od trasy i daty. Sprawdź liczbę przesiadek w ofercie; sama strona kierunku nie potwierdza bezpośredniego połączenia." },
  { q: "Jak nie przepłacić za bagaż?", a: "Porównaj zakres taryfy, liczbę sztuk, wymiary i wagę bagażu. Mała torba pod siedzeniem i większa walizka kabinowa to różne usługi. Dodatki mogą być płatne osobno." },
  { q: "Czy cena z okazji jest ceną końcową?", a: "Okazje pochodzą z zapisanych wyszukiwań Aviasales. Cena może się zmienić. Cenę końcową, bagaż i inne dodatki potwierdź u sprzedawcy przed płatnością." },
];
